import React from 'react';
import { Phone, MapPin, Instagram, Facebook, Mail } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-[#050505] border-t border-white/10 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00D9FF] via-[#5B7FFF] to-[#FF1493] to-transparent" />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div className="animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={businessInfo.logo} 
                alt="DNA Barbershop" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {t.footer.description}
            </p>
            {/* Social links */}
            <div className="flex items-center gap-3">
              <a 
                href="#" 
                className="w-10 h-10 rounded-full glass-effect border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#00D9FF] hover:border-[#00D9FF]/50 transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full glass-effect border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#FF1493] hover:border-[#FF1493]/50 transition-all duration-300 hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <h4 className="text-white font-semibold mb-4 text-lg">{t.footer.navigation}</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="#accueil"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.home}
                </a>
              </li>
              <li>
                <a
                  href="#equipe"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.team}
                </a>
              </li>
              <li>
                <a
                  href="#services"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.services}
                </a>
              </li>
              <li>
                <a
                  href="#avis"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.reviews}
                </a>
              </li>
              <li>
                <a
                  href="#galerie"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.gallery}
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  className="text-gray-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 text-sm inline-block"
                >
                  {t.nav.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h4 className="text-white font-semibold mb-4 text-lg">{t.footer.contact}</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${businessInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-start gap-2 text-gray-400 hover:text-[#00D9FF] transition-colors duration-200 text-sm group"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:animate-pulse" />
                  <span>{businessInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-start gap-2 text-gray-400 hover:text-[#00D9FF] transition-colors duration-200 text-sm group"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:animate-pulse" />
                  <span>{businessInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={businessInfo.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-gray-400 hover:text-[#FF1493] transition-colors duration-200 text-sm group"
                >
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:animate-pulse" />
                  <span>{businessInfo.address}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} DNA Barbershop. {t.footer.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
